"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import DataTable, { type Column, type FilterOption } from "../../components/commons/DataTable";
import ExportProgressModal from "../../components/commons/ExportProgressModal";
import Button from "../../components/commons/Button";
import { Eye, DocumentDownload } from "iconsax-reactjs";
import {
  getAuthorizationCredentialById,
  getAuthorizationCredentials,
} from "../../services/webServices/sepehr/authorization-credentials/get";
import { patchAuthorizationCredential } from "../../services/webServices/sepehr/authorization-credentials/patch";
import type {
  AuthorizationCredential,
  AuthorizeUserRole,
} from "../../services/webServices/sepehr/authorization-credentials/types";
import { getGradeLabel } from "../../constants/education";
import RejectReasonModal from "./RejectReasonModal";
import RejectionReasonDisplay from "./RejectionReasonDisplay";
import type { RejectionReasonPayload } from "../../services/webServices/sepehr/authorization-credentials/rejection-reason";
import {
  buildExportBody,
  runExport,
} from "../../services/webServices/export/post";
import {
  EXPORT_FIELD_OPTIONS,
  type ExportProgressStage,
} from "../../services/webServices/export/types";

const STATUS_MAP = {
  0: { label: "در انتظار بررسی", color: "bg-yellow-100 text-yellow-700" },
  1: { label: "تایید شده", color: "bg-green-100 text-green-700" },
  2: { label: "رد شده", color: "bg-red-100 text-red-700" },
};

function getCredentialFullName(credential: AuthorizationCredential): string {
  const fromUser = credential.user.full_name?.trim();
  if (fromUser) return fromUser;

  const parts = [credential.first_name, credential.last_name]
    .map((part) => part?.trim())
    .filter(Boolean);

  return parts.length > 0 ? parts.join(" ") : "-";
}

function VerificationStatusTag({ status }: { status: number }) {
  const config = STATUS_MAP[status as keyof typeof STATUS_MAP];

  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
        config?.color || "bg-gray-100 text-gray-700"
      }`}
    >
      {config?.label || "نامشخص"}
    </span>
  );
}

const TEACHER_ASSIGNED_ROLE_MAP = {
  "1": { label: "معلم", color: "bg-blue-100 text-blue-700" },
  "2": { label: "همیار", color: "bg-purple-100 text-purple-700" },
} as const;

function normalizeTeacherAssignedRole(
  role: AuthorizeUserRole | number | null | undefined,
): keyof typeof TEACHER_ASSIGNED_ROLE_MAP | null {
  if (role === "1" || role === 1) return "1";
  if (role === "2" || role === 2) return "2";
  return null;
}

function TeacherAssignedRoleTag({
  role,
  verificationStatus,
}: {
  role: AuthorizeUserRole | number | null | undefined;
  verificationStatus: number;
}) {
  if (verificationStatus !== 1) {
    return (
      <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
        بدون نقش
      </span>
    );
  }

  const normalizedRole = normalizeTeacherAssignedRole(role);

  if (!normalizedRole) {
    return (
      <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
        بدون نقش
      </span>
    );
  }

  const { label, color } = TEACHER_ASSIGNED_ROLE_MAP[normalizedRole];

  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${color}`}
    >
      {label}
    </span>
  );
}

const FILE_URL = process.env.REACT_APP_File_URL || "";
const PAGE_SIZE = 10;

const statusFilterOptions: FilterOption[] = [
  { value: "0", label: "در انتظار بررسی" },
  { value: "1", label: "تایید شده" },
  { value: "2", label: "رد شده" },
];

const teacherRoleFilterOptions: FilterOption[] = [
  { value: "1", label: "معلم" },
  { value: "2", label: "همیار" },
];

function hasFile(filePath: string | null | undefined): filePath is string {
  return Boolean(filePath?.trim());
}

interface CredentialFieldProps {
  label: string;
  value: string;
}

function CredentialField({ label, value }: CredentialFieldProps) {
  return (
    <div className="rounded-xl border border-gray-200 p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-sm font-medium text-gray-800">{value || "-"}</p>
    </div>
  );
}

function getFileName(filePath: string): string {
  return filePath.split("/").pop() || "resume";
}

async function downloadFile(filePath: string): Promise<void> {
  const url = `${FILE_URL}${filePath}`;
  const fileName = getFileName(filePath);

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Download failed");
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

interface CredentialFileDownloadProps {
  label: string;
  filePath: string | null | undefined;
  downloadLabel?: string;
}

function CredentialFileDownload({
  label,
  filePath,
  downloadLabel = "دانلود فایل",
}: CredentialFileDownloadProps) {
  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="mb-2 text-sm font-semibold text-gray-800">{label}</p>
      {hasFile(filePath) ? (
        <button
          type="button"
          onClick={() => downloadFile(filePath)}
          className="flex w-full items-center justify-between gap-3 rounded-lg bg-blue-50 px-4 py-3 text-blue-600 transition-colors hover:bg-blue-100"
        >
          <span className="truncate text-sm font-medium">
            {getFileName(filePath)}
          </span>
          <span className="flex shrink-0 items-center gap-2 text-sm font-medium">
            {downloadLabel}
            <DocumentDownload size={20} />
          </span>
        </button>
      ) : (
        <p className="text-sm text-gray-500">فایلی ثبت نشده است.</p>
      )}
    </div>
  );
}

interface CredentialFilePreviewProps {
  label: string;
  filePath: string | null | undefined;
  alt: string;
  onPreview: (url: string) => void;
}

function CredentialFilePreview({
  label,
  filePath,
  alt,
  onPreview,
}: CredentialFilePreviewProps) {
  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="mb-2 text-sm font-semibold text-gray-800">{label}</p>
      {hasFile(filePath) ? (
        <img
          src={`${FILE_URL}${filePath}`}
          alt={alt}
          className="max-h-72 w-full cursor-pointer rounded-lg border border-gray-200 object-contain transition-opacity hover:opacity-90"
          loading="lazy"
          decoding="async"
          onClick={() => onPreview(`${FILE_URL}${filePath}`)}
        />
      ) : (
        <p className="text-sm text-gray-500">فایلی ثبت نشده است.</p>
      )}
    </div>
  );
}

interface StudentCredentialDetailsProps {
  credential: AuthorizationCredential;
  loading: boolean;
  onApprove: (id: number) => void;
  onReject: () => void;
}

function StudentCredentialDetails({
  credential,
  loading,
  onApprove,
  onReject,
}: StudentCredentialDetailsProps) {
  const { user } = credential;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CredentialField
          label="نام و نام خانوادگی"
          value={getCredentialFullName(credential)}
        />
        <CredentialField label="استان" value={credential.province || "-"} />
        <CredentialField label="شهر" value={credential.city || "-"} />
        <CredentialField
          label="پایه تحصیلی"
          value={getGradeLabel(credential.education_grade)}
        />
        <CredentialField
          label="شماره موبایل"
          value={user.phone_number || "-"}
        />
        <CredentialField label="ایمیل" value={user.email || "-"} />
        <div className="rounded-xl border border-gray-200 p-3">
          <p className="text-xs text-gray-500">وضعیت احراز</p>
          <div className="mt-2">
            <VerificationStatusTag status={credential.verification_status} />
          </div>
        </div>
        {credential.verification_status === 2 && credential.rejection_reason && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 sm:col-span-2">
            <p className="text-xs text-red-600">دلیل رد</p>
            <RejectionReasonDisplay rejectionReason={credential.rejection_reason} />
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-end justify-end gap-3">
        <Button
          variant="primary"
          style="solid"
          size="md"
          text="تایید"
          loading={loading}
          disabled={loading}
          onClick={() => onApprove(credential.id)}
        />
        <Button
          variant="primary"
          style="solid"
          size="md"
          text="رد"
          loading={loading}
          disabled={loading}
          onClick={onReject}
          customClassName="bg-red-500 hover:bg-red-600 disabled:bg-red-400"
        />
      </div>
    </div>
  );
}

interface StaffCredentialDetailsProps {
  credential: AuthorizationCredential;
  loading: boolean;
  onApproveAsTeacher: (id: number) => void;
  onApproveAsHelper: (id: number) => void;
  onReject: () => void;
  onPreviewImage: (url: string) => void;
}

function TeacherCredentialDetails({
  credential,
  loading,
  onApproveAsTeacher,
  onApproveAsHelper,
  onReject,
  onPreviewImage,
}: StaffCredentialDetailsProps) {
  const { user } = credential;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CredentialField
          label="نام و نام خانوادگی"
          value={getCredentialFullName(credential)}
        />
        <CredentialField
          label="کد ملی"
          value={credential.national_code || "-"}
        />
        <CredentialField label="استان" value={credential.province || "-"} />
        <CredentialField label="شهر" value={credential.city || "-"} />
        <CredentialField
          label="نام مدرسه"
          value={credential.school_name || "-"}
        />
        <CredentialField
          label="پایه تدریس"
          value={getGradeLabel(credential.teaching_grade)}
        />
        <CredentialField
          label="شماره موبایل"
          value={user.phone_number || "-"}
        />
        <CredentialField label="ایمیل" value={user.email || "-"} />
        <div className="rounded-xl border border-gray-200 p-3">
          <p className="text-xs text-gray-500">وضعیت احراز</p>
          <div className="mt-2">
            <VerificationStatusTag status={credential.verification_status} />
          </div>
        </div>
        {credential.verification_status === 2 && credential.rejection_reason && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 sm:col-span-2">
            <p className="text-xs text-red-600">دلیل رد</p>
            <RejectionReasonDisplay rejectionReason={credential.rejection_reason} />
          </div>
        )}
      </div>

      <CredentialFilePreview
        label="عکس کارت ملی"
        filePath={credential.identity_file}
        alt="national-card"
        onPreview={onPreviewImage}
      />

      <CredentialFilePreview
        label="حکم کارگزینی"
        filePath={credential.employment_decree_file}
        alt="employment-decree"
        onPreview={onPreviewImage}
      />

      <CredentialFileDownload
        label="رزومه"
        filePath={credential.resume_file || credential.resume}
        downloadLabel="دانلود رزومه"
      />

      <div className="mt-4 flex flex-wrap items-end justify-end gap-3">
        <Button
          variant="primary"
          style="solid"
          size="md"
          text="تایید (همیار)"
          loading={loading}
          disabled={loading}
          onClick={() => onApproveAsHelper(credential.id)}
          customClassName="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400"
        />
        <Button
          variant="primary"
          style="solid"
          size="md"
          text="تایید معلم"
          loading={loading}
          disabled={loading}
          onClick={() => onApproveAsTeacher(credential.id)}
        />
        <Button
          variant="primary"
          style="solid"
          size="md"
          text="رد"
          loading={loading}
          disabled={loading}
          onClick={onReject}
          customClassName="bg-red-500 hover:bg-red-600 disabled:bg-red-400"
        />
      </div>
    </div>
  );
}

function AssistantCredentialDetails({
  credential,
  loading,
  onApprove,
  onReject,
  onPreviewImage,
}: {
  credential: AuthorizationCredential;
  loading: boolean;
  onApprove: (id: number) => void;
  onReject: () => void;
  onPreviewImage: (url: string) => void;
}) {
  const { user } = credential;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CredentialField
          label="نام و نام خانوادگی"
          value={getCredentialFullName(credential)}
        />
        <CredentialField
          label="کد ملی"
          value={credential.national_code || "-"}
        />
        <CredentialField label="استان" value={credential.province || "-"} />
        <CredentialField label="شهر" value={credential.city || "-"} />
        <CredentialField
          label="نام مدرسه"
          value={credential.school_name || "-"}
        />
        <CredentialField
          label="پایه تدریس"
          value={getGradeLabel(credential.teaching_grade)}
        />
        <CredentialField
          label="شماره موبایل"
          value={user.phone_number || "-"}
        />
        <CredentialField label="ایمیل" value={user.email || "-"} />
        <div className="rounded-xl border border-gray-200 p-3">
          <p className="text-xs text-gray-500">وضعیت احراز</p>
          <div className="mt-2">
            <VerificationStatusTag status={credential.verification_status} />
          </div>
        </div>
        {credential.verification_status === 2 && credential.rejection_reason && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 sm:col-span-2">
            <p className="text-xs text-red-600">دلیل رد</p>
            <RejectionReasonDisplay rejectionReason={credential.rejection_reason} />
          </div>
        )}
      </div>

      <CredentialFilePreview
        label="عکس کارت ملی"
        filePath={credential.identity_file}
        alt="national-card"
        onPreview={onPreviewImage}
      />

      <CredentialFilePreview
        label="حکم کارگزینی"
        filePath={credential.employment_decree_file}
        alt="employment-decree"
        onPreview={onPreviewImage}
      />

      <CredentialFileDownload
        label="رزومه"
        filePath={credential.resume_file || credential.resume}
        downloadLabel="دانلود رزومه"
      />

      <div className="mt-4 flex flex-wrap items-end justify-end gap-3">
        <Button
          variant="primary"
          style="solid"
          size="md"
          text="تایید"
          loading={loading}
          disabled={loading}
          onClick={() => onApprove(credential.id)}
          customClassName="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400"
        />
        <Button
          variant="primary"
          style="solid"
          size="md"
          text="رد"
          loading={loading}
          disabled={loading}
          onClick={onReject}
          customClassName="bg-red-500 hover:bg-red-600 disabled:bg-red-400"
        />
      </div>
    </div>
  );
}

type AuthorizationPageRole = "0" | "1" | "2";

interface SepehrJoAuthorizationProps {
  authorizeUserRole: AuthorizationPageRole;
}

const PAGE_TITLES: Record<AuthorizationPageRole, string> = {
  "0": "احراز هویت دانش‌آموز",
  "1": "احراز هویت معلم",
  "2": "احراز هویت همیار",
};

const MODAL_TITLES: Record<AuthorizationPageRole, string> = {
  "0": "جزئیات احراز هویت دانش‌آموز",
  "1": "جزئیات احراز هویت معلم",
  "2": "جزئیات احراز هویت همیار",
};

function isStaffAuthorizationRole(role: AuthorizationPageRole): boolean {
  return role === "1" || role === "2";
}

export default function SepehrJoAuthorization({
  authorizeUserRole,
}: SepehrJoAuthorizationProps) {
  const [credentials, setCredentials] = useState<AuthorizationCredential[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCredential, setSelectedCredential] =
    useState<AuthorizationCredential | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgressOpen, setExportProgressOpen] = useState(false);
  const [exportProgressStage, setExportProgressStage] =
    useState<ExportProgressStage>("submitting");
  const [exportProgressMessage, setExportProgressMessage] = useState("");
  const fetchAbortRef = useRef<AbortController | null>(null);

  const fetchCredentials = useCallback(
    async (
      page: number,
      search?: string,
      status?: string,
      roleFilterParam?: string,
    ) => {
      fetchAbortRef.current?.abort();
      const controller = new AbortController();
      fetchAbortRef.current = controller;

      setLoading(true);
      try {
        const resolvedStatus = status !== undefined ? status : statusFilter;
        const resolvedSearch = search !== undefined ? search : searchValue;
        const resolvedRoleFilter =
          roleFilterParam !== undefined ? roleFilterParam : roleFilter;
        const parsedStatus = resolvedStatus
          ? (Number(resolvedStatus) as 0 | 1 | 2)
          : undefined;

        const commonParams = {
          page,
          page_size: PAGE_SIZE,
          ...(resolvedSearch.trim() && { search: resolvedSearch.trim() }),
          ...(parsedStatus !== undefined && {
            verification_status: parsedStatus,
          }),
        };

        if (authorizeUserRole === "0") {
          const response = await getAuthorizationCredentials({
            params: {
              ...commonParams,
              authorize_user_role: 0,
            },
            signal: controller.signal,
          });

          setCredentials(response.results || []);
          setTotalItems(response.count || 0);
          return;
        }

        if (authorizeUserRole === "2") {
          const response = await getAuthorizationCredentials({
            params: {
              ...commonParams,
              authorize_user_role: 2,
            },
            signal: controller.signal,
          });

          setCredentials(response.results || []);
          setTotalItems(response.count || 0);
          return;
        }

        const authorizeUserRoleParam: Array<1 | 2> =
          resolvedRoleFilter === "1" || resolvedRoleFilter === "2"
            ? [Number(resolvedRoleFilter) as 1 | 2]
            : [1, 2];

        const response = await getAuthorizationCredentials({
          params: {
            ...commonParams,
            authorize_user_role: authorizeUserRoleParam,
          },
          signal: controller.signal,
        });

        setCredentials(response.results || []);
        setTotalItems(response.count || 0);
      } catch (error) {
        if (axios.isCancel(error)) return;

        console.error("Error fetching authorization credentials:", error);
        setCredentials([]);
        setTotalItems(0);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [authorizeUserRole, searchValue, statusFilter, roleFilter],
  );

  useEffect(() => {
    setSearchValue("");
    setStatusFilter("");
    setRoleFilter("");
    setCurrentPage(1);
    fetchCredentials(1, "", "", "");

    return () => {
      fetchAbortRef.current?.abort();
    };
  }, [authorizeUserRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      setCurrentPage(1);
      fetchCredentials(1, value);
    },
    [fetchCredentials],
  );

  const handleStatusFilterChange = useCallback(
    (value: string) => {
      setStatusFilter(value);
      setCurrentPage(1);
      fetchCredentials(1, searchValue, value, roleFilter);
    },
    [fetchCredentials, searchValue, roleFilter],
  );

  const handleRoleFilterChange = useCallback(
    (value: string) => {
      setRoleFilter(value);
      setCurrentPage(1);
      fetchCredentials(1, searchValue, statusFilter, value);
    },
    [fetchCredentials, searchValue, statusFilter],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchCredentials(page, searchValue, statusFilter, roleFilter);
    },
    [fetchCredentials, searchValue, statusFilter, roleFilter],
  );

  const handleOpenModal = useCallback(async (credential: AuthorizationCredential) => {
    setIsModalOpen(true);
    setDetailLoading(true);
    try {
      const detail = await getAuthorizationCredentialById({ id: credential.id });
      setSelectedCredential(detail || credential);
    } catch {
      setSelectedCredential(credential);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setIsRejectModalOpen(false);
    setTimeout(() => setSelectedCredential(null), 300);
  }, []);

  const handleOpenRejectModal = useCallback(() => {
    setIsRejectModalOpen(true);
  }, []);

  const handleCloseRejectModal = useCallback(() => {
    setIsRejectModalOpen(false);
  }, []);

  const handleReviewCredential = useCallback(
    async (
      credentialId: number,
      verificationStatus: 0 | 1 | 2,
      authorizeUserRole?: AuthorizeUserRole,
      rejectionReasonPayload?: RejectionReasonPayload | null,
    ) => {
      setModalLoading(true);
      try {
        const result = await patchAuthorizationCredential({
          id: credentialId,
          body: {
            verification_status: verificationStatus,
            ...(authorizeUserRole && { authorize_user_role: authorizeUserRole }),
            ...(verificationStatus === 2 && {
              rejection_reason: rejectionReasonPayload || null,
            }),
          },
        });

        if (result) {
          setSelectedCredential(result);
          fetchCredentials(currentPage, searchValue, statusFilter, roleFilter);
          setIsRejectModalOpen(false);
          setTimeout(() => handleCloseModal(), 300);
        }
      } catch (error) {
        console.error("Error updating authorization credential:", error);
      } finally {
        setModalLoading(false);
      }
    },
    [
      currentPage,
      fetchCredentials,
      handleCloseModal,
      searchValue,
      statusFilter,
      roleFilter,
    ],
  );

  const handleConfirmReject = useCallback(
    (payload: RejectionReasonPayload) => {
      if (!selectedCredential) return;

      handleReviewCredential(selectedCredential.id, 2, undefined, payload);
    },
    [handleReviewCredential, selectedCredential],
  );

  const handleExport = useCallback(async () => {
    setExportLoading(true);
    setExportProgressOpen(true);
    setExportProgressStage("submitting");
    setExportProgressMessage("در حال ثبت درخواست خروجی...");
    try {
      const exportRoles =
        authorizeUserRole === "0"
          ? [0]
          : authorizeUserRole === "2"
            ? [2]
            : roleFilter
              ? [Number(roleFilter)]
              : [1, 2];

      await runExport(
        "authorizations",
        buildExportBody(EXPORT_FIELD_OPTIONS.authorizations, {
          roles: exportRoles,
          ...(statusFilter && {
            verification_status: Number(statusFilter),
          }),
        }),
        {
          onProgress: ({ stage, message }) => {
            setExportProgressStage(stage);
            setExportProgressMessage(message);
          },
        },
      );
      toast.success("خروجی با موفقیت دانلود شد");
    } catch (error) {
      console.error("Error exporting authorization credentials:", error);
      toast.error("خطا در تهیه خروجی");
    } finally {
      setExportLoading(false);
      setExportProgressOpen(false);
    }
  }, [authorizeUserRole, statusFilter, roleFilter]);

  const columns: Column<AuthorizationCredential>[] = useMemo(() => {
    const baseColumns: Column<AuthorizationCredential>[] = [
      {
        key: "full_name",
        header: "نام و نام خانوادگی",
        accessor: (row) => getCredentialFullName(row),
        width: "180px",
      },
      {
        key: "province",
        header: "استان",
        accessor: (row) => row.province || "-",
        width: "120px",
      },
      {
        key: "city",
        header: "شهر",
        accessor: (row) => row.city || "-",
        width: "120px",
      },
      {
        key: "grade",
        header: "پایه",
        accessor: (row) => {
          const grade = isStaffAuthorizationRole(authorizeUserRole)
            ? row.teaching_grade
            : row.education_grade;
          return getGradeLabel(grade);
        },
        width: "100px",
        align: "center",
      },
      {
        key: "phone_number",
        header: "شماره موبایل",
        accessor: (row) => row.user.phone_number || "-",
        width: "140px",
      },
    ];

    const teacherRoleColumn: Column<AuthorizationCredential> = {
      key: "authorize_user_role",
      header: "نقش",
      accessor: (row) => (
        <TeacherAssignedRoleTag
          role={row.authorize_user_role}
          verificationStatus={row.verification_status}
        />
      ),
      width: "110px",
      align: "center",
    };

    const trailingColumns: Column<AuthorizationCredential>[] = [
      {
        key: "verification_status",
        header: "وضعیت احراز",
        accessor: (row) => {
          const status =
            STATUS_MAP[row.verification_status as keyof typeof STATUS_MAP];
          return (
            <span
              className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                status?.color || "bg-gray-100 text-gray-700"
              }`}
            >
              {status?.label || "نامشخص"}
            </span>
          );
        },
        width: "140px",
        align: "center",
      },
      {
        key: "details",
        header: "جزئیات",
        accessor: (row) => (
          <button
            onClick={() => handleOpenModal(row)}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-blue-600 transition-colors hover:bg-blue-100"
            title="مشاهده جزئیات"
          >
            <Eye size="18" />
          </button>
        ),
        width: "100px",
        align: "right",
      },
    ];

    if (authorizeUserRole === "1") {
      return [...baseColumns, teacherRoleColumn, ...trailingColumns];
    }

    return [...baseColumns, ...trailingColumns];
  }, [authorizeUserRole, handleOpenModal]);

  return (
    <div className="w-full" dir="rtl">
      <DataTable<AuthorizationCredential>
        data={credentials}
        columns={columns}
        title={PAGE_TITLES[authorizeUserRole]}
        searchable
        searchPlaceholder="جستجو بر اساس نام، ایمیل یا شماره موبایل..."
        onSearchChange={handleSearchChange}
        searchValue={searchValue}
        filterable
        filterPlaceholder="همه وضعیت‌ها"
        filterOptions={statusFilterOptions}
        filterValue={statusFilter}
        onFilterChange={handleStatusFilterChange}
        sortable={authorizeUserRole === "1"}
        sortOptions={authorizeUserRole === "1" ? teacherRoleFilterOptions : []}
        sortValue={roleFilter}
        sortPlaceholder="همه نقش‌ها"
        onSortChange={
          authorizeUserRole === "1" ? handleRoleFilterChange : undefined
        }
        exportable
        exportLabel="خروجی"
        onExportClick={handleExport}
        exportLoading={exportLoading}
        loading={loading}
        loadingText="در حال بارگذاری درخواست‌های احراز هویت..."
        emptyText="درخواستی یافت نشد"
        pagination
        pageSize={PAGE_SIZE}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        serverSidePagination
      />

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">
                {MODAL_TITLES[authorizeUserRole]}
              </h2>
              <button
                onClick={handleCloseModal}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                ✕
              </button>
            </div>

            {detailLoading || !selectedCredential ? (
              <div className="flex flex-col gap-4 animate-pulse">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {Array.from({
                    length: isStaffAuthorizationRole(authorizeUserRole) ? 6 : 4,
                  }).map((_, index) => (
                    <div
                      key={index}
                      className="rounded-xl border border-gray-200 p-3"
                    >
                      <div className="h-3 w-20 rounded bg-gray-200" />
                      <div className="mt-2 h-4 w-36 rounded bg-gray-200" />
                    </div>
                  ))}
                </div>
                {isStaffAuthorizationRole(authorizeUserRole) && (
                  <>
                    <div className="rounded-xl border border-gray-200 p-4">
                      <div className="mb-2 h-4 w-28 rounded bg-gray-200" />
                      <div className="h-60 w-full rounded-lg border border-gray-200 bg-gray-200" />
                    </div>
                    <div className="rounded-xl border border-gray-200 p-4">
                      <div className="mb-2 h-4 w-28 rounded bg-gray-200" />
                      <div className="h-60 w-full rounded-lg border border-gray-200 bg-gray-200" />
                    </div>
                  </>
                )}
                <div className="mt-4 flex items-end justify-end gap-3">
                  <div className="h-10 w-28 rounded-lg bg-gray-200" />
                  {authorizeUserRole === "1" && (
                    <div className="h-10 w-28 rounded-lg bg-gray-200" />
                  )}
                  <div className="h-10 w-20 rounded-lg bg-gray-200" />
                </div>
              </div>
            ) : authorizeUserRole === "0" ? (
              <StudentCredentialDetails
                credential={selectedCredential}
                loading={modalLoading}
                onApprove={(id) => handleReviewCredential(id, 1)}
                onReject={handleOpenRejectModal}
              />
            ) : authorizeUserRole === "1" ? (
              <TeacherCredentialDetails
                credential={selectedCredential}
                loading={modalLoading}
                onApproveAsTeacher={(id) => handleReviewCredential(id, 1, "1")}
                onApproveAsHelper={(id) => handleReviewCredential(id, 1, "2")}
                onReject={handleOpenRejectModal}
                onPreviewImage={setPreviewImage}
              />
            ) : (
              <AssistantCredentialDetails
                credential={selectedCredential}
                loading={modalLoading}
                onApprove={(id) => handleReviewCredential(id, 1, "2")}
                onReject={handleOpenRejectModal}
                onPreviewImage={setPreviewImage}
              />
            )}
          </div>
        </div>
      )}

      <RejectReasonModal
        open={isRejectModalOpen}
        loading={modalLoading}
        authorizeUserRole={authorizeUserRole}
        onConfirm={handleConfirmReject}
        onClose={handleCloseRejectModal}
      />

      <ExportProgressModal
        open={exportProgressOpen}
        stage={exportProgressStage}
        message={exportProgressMessage}
      />

      {previewImage && (
        <div
          className="fixed inset-0 z-[1200] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative flex h-[min(82vh,720px)] w-full max-w-5xl items-center justify-center rounded-2xl px-12 py-14 sm:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 z-10 text-white text-sm border border-white/40 rounded-md px-3 py-1 bg-black/35"
            >
              بستن
            </button>
            <img
              src={previewImage}
              alt="preview-image"
              className="h-full max-h-full w-full max-w-full object-contain rounded-xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
